import { useEffect, useRef, useState } from 'react';
import { Radio, message, notification } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ImportFilter,
  ImportStatus,
  PropertyMapping,
  TaskonioField,
  UserMapping,
} from '../../../../types/Import';
import { useGetImport, useUpdateImport, useExecuteImport } from '../../../../serverState/imports';
import { useGetUsersWithWorkspaceAccess } from '../../../../serverState/workspace';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';
import { ImportMappingSummary } from './ImportMappingSummary';
import { ImportMappingProperties } from './ImportMappingProperties';
import { ImportMappingUsers } from './ImportMappingUsers';
import { BaseButton } from '../../../../components/UI/BaseButton';

interface ImportMappingScreenProps {
  importId: number;
  workspaceId: number;
  onBack: () => void;
  onCloseModal: () => void;
}

const STATUS_BADGE: Record<string, { label: string; dotColor: string; textColor: string }> = {
  [ImportStatus.PENDING]: { label: 'In Progress', dotColor: 'bg-orange-400', textColor: 'text-orange-600' },
  [ImportStatus.PARSED]: { label: 'In Progress', dotColor: 'bg-orange-400', textColor: 'text-orange-600' },
  [ImportStatus.MAPPING]: { label: 'In Progress', dotColor: 'bg-orange-400', textColor: 'text-orange-600' },
  [ImportStatus.IN_PROGRESS]: { label: 'In Progress', dotColor: 'bg-orange-400', textColor: 'text-orange-600' },
  [ImportStatus.DONE]: { label: 'Done', dotColor: 'bg-green-500', textColor: 'text-green-700' },
  [ImportStatus.FAILED]: { label: 'Failed', dotColor: 'bg-red-500', textColor: 'text-red-600' },
};

export const ImportMappingScreen = ({ importId, workspaceId, onBack, onCloseModal }: ImportMappingScreenProps) => {
  const importQuery = useGetImport(importId);
  const workspaceUsersQuery = useGetUsersWithWorkspaceAccess(workspaceId);
  const updateMutation = useUpdateImport();
  const executeMutation = useExecuteImport();

  const [localFilter, setLocalFilter] = useState<ImportFilter>(ImportFilter.ALL);
  const [localMappings, setLocalMappings] = useState<PropertyMapping[]>([]);
  const [localUserMappings, setLocalUserMappings] = useState<UserMapping[]>([]);
  const [addUnsorted, setAddUnsorted] = useState(false);
  const [dateFormat, setDateFormat] = useState('auto');
  const [autoMappedFields, setAutoMappedFields] = useState<Set<TaskonioField>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const importRecord = importQuery.data;

  // Initialize local state from import record
  useEffect(() => {
    if (importRecord && !initialized) {
      setLocalFilter(importRecord.importFilter);
      setLocalMappings(importRecord.propertyMappings);
      setLocalUserMappings(importRecord.userMappings);
      setAddUnsorted(importRecord.addUnsortedToDescription);

      // Track which fields were auto-mapped
      const autoFields = new Set<TaskonioField>();
      importRecord.propertyMappings.forEach((m) => {
        if (m.fileColumnIndex !== null) {
          autoFields.add(m.taskonioField);
        }
      });
      setAutoMappedFields(autoFields);
      setInitialized(true);
    }
  }, [importRecord, initialized]);

  const handleMappingChange = (taskonioField: TaskonioField, fileColumnIndex: number | null) => {
    setLocalMappings((prev) =>
      prev.map((m) => (m.taskonioField === taskonioField ? { ...m, fileColumnIndex } : m))
    );
    setAutoMappedFields((prev) => {
      const next = new Set(prev);
      next.delete(taskonioField);
      return next;
    });
  };

  const handleUserMappingChange = (workspaceUserId: number, fileUserName: string | null) => {
    setLocalUserMappings((prev) =>
      prev.map((m) => (m.workspaceUserId === workspaceUserId ? { ...m, fileUserName } : m))
    );
  };

  const taskNameMapped = localMappings.some(
    (m) => m.taskonioField === 'taskName' && m.fileColumnIndex !== null
  );

  const handleExecute = () => {
    if (!taskNameMapped) {
      message.error('Task Name mapping is required');
      return;
    }

    updateMutation.mutate(
      {
        importId,
        data: {
          importFilter: localFilter,
          propertyMappings: localMappings,
          userMappings: localUserMappings,
          addUnsortedToDescription: addUnsorted,
          dateFormat,
        },
      },
      {
        onSuccess: () => {
          executeMutation.mutate(
            { importId },
            {
              onSuccess: () => {
                notification.open({
                  key: 'import-progress',
                  message: 'Importing tasks...',
                  description: "We'll let you know when all tasks are created.",
                  icon: <LoadingOutlined style={{ color: '#2563eb' }} />,
                  placement: 'topRight',
                  duration: 0,
                  style: { backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 },
                });
                onCloseModal();
              },
              onError: (err) => {
                const errorMessage =
                  (err as any)?.response?.data?.message || 'Failed to start import';
                message.error(errorMessage);
              },
            }
          );
        },
        onError: () => {
          message.error('Failed to save mappings. Please try again.');
        },
      }
    );
  };

  const isSaving = executeMutation.isPending || updateMutation.isPending;
  const isFailed = importRecord?.status === ImportStatus.FAILED;

  if (importQuery.isLoading) {
    return (
      <div className="w-full h-[556px] flex items-center justify-center">
        <div className="text-neutral-700 text-sm">Loading import...</div>
      </div>
    );
  }

  if (!importRecord) {
    return (
      <div className="w-full h-[556px] flex items-center justify-center">
        <div className="text-neutral-700 text-sm">Import not found</div>
      </div>
    );
  }

  const badge = STATUS_BADGE[importRecord.status];

  if (isFailed) {
    return (
      <div className="w-full h-[556px] flex flex-col items-center justify-center gap-4 px-8">
        <div className="text-red-600 text-base font-semibold">Import Failed</div>
        <div className="text-neutral-700 text-sm">{importRecord.error || 'An unknown error occurred'}</div>
        <div className="flex gap-2">
          <BaseButton type="secondary" size="medium" onClick={onBack}>
            Go back
          </BaseButton>
          <BaseButton type="accent" size="medium" onClick={handleExecute}>
            Retry
          </BaseButton>
        </div>
      </div>
    );
  }

  const importedByAvatarUrl = getUserProfilePictureUrl(importRecord.createdByUser.profilePicturePath ?? null);

  return (
    <div className="w-full h-[556px] flex flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 pl-6 pr-12 pt-4 pb-3 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="flex items-center justify-center w-7 h-7 rounded hover:bg-neutral-100 cursor-pointer bg-transparent border-none"
              onClick={onBack}
            >
              <ArrowLeftOutlined className="text-brand-grey-dark" />
            </button>
            <div className="flex items-center gap-2 text-brand-grey-dark text-sm">
              <span className="font-semibold">Import by</span>
              {importedByAvatarUrl ? (
                <img src={importedByAvatarUrl} alt="" className="w-5 h-5 rounded-full" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-neutral-300 flex items-center justify-center text-[10px] text-neutral-700">
                  {importRecord.createdByUser.fullName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-semibold">{importRecord.createdByUser.fullName}</span>
              <span className="text-neutral-500">, {dayjs(importRecord.createdAt).format('MMM DD, YYYY')}</span>
            </div>
          </div>
          {badge && (
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${badge.dotColor}`} />
              <span className={`text-xs font-medium ${badge.textColor}`}>{badge.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-4" ref={scrollRef}>
        <div className="bg-neutral-100 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-brand-grey-dark text-sm">Import:</span>
            <Radio.Group
              value={localFilter}
              onChange={(e) => setLocalFilter(e.target.value)}
            >
              <Radio value={ImportFilter.ALL}>
                <span className="text-brand-grey-dark text-sm">All Tasks</span>
              </Radio>
              <Radio value={ImportFilter.ACTIVE_ONLY}>
                <span className="text-brand-grey-dark text-sm">Active Tasks Only</span>
              </Radio>
            </Radio.Group>
          </div>

          <ImportMappingSummary summary={importRecord.summary} />
        </div>

        <ImportMappingProperties
          propertyMappings={localMappings}
          fileColumns={importRecord.fileColumns}
          autoMappedFields={autoMappedFields}
          addUnsorted={addUnsorted}
          onAddUnsortedChange={setAddUnsorted}
          onMappingChange={handleMappingChange}
        />

        <ImportMappingUsers
          userMappings={localUserMappings}
          fileUsers={importRecord.fileUsers}
          workspaceUsers={workspaceUsersQuery.data}
          onUserMappingChange={handleUserMappingChange}
        />
      </div>

      {/* Sticky footer */}
      <div className="shrink-0 px-6 py-3 border-t border-neutral-200 flex items-center justify-between">
        <BaseButton type="text" size="medium" danger onClick={onBack}>
          <DeleteOutlined />
          Discard Import
        </BaseButton>

        <BaseButton
          type="primary"
          size="medium"
          disabled={!taskNameMapped || isSaving}
          disabledTooltip={!taskNameMapped ? 'Task Name mapping is required' : undefined}
          onClick={handleExecute}
        >
          {isSaving ? 'Starting import...' : 'Complete and Create Tasks'}
          {!isSaving && <ArrowRightOutlined />}
        </BaseButton>
      </div>
    </div>
  );
};
